import { CartAddEvent, CartUpdateEvent, CartErrorEvent } from '@theme/events';
import { createQuoteController } from './avl-quote.js';
import { readStockMap, stockForVariant, refreshStock } from './avl-product-stock.js';

// Shopify remains authoritative for prices, allocations, inventory and cart totals.
// This view deliberately uses the existing standard Appstle group, not Smagsklub.
class AvlProduct extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    try { this.product = JSON.parse(this.querySelector('[data-product-data]').textContent); }
    catch { return; }
    this.stockMap = readStockMap(this);
    this.form = this.querySelector('[data-avl-product-form]');
    this.quantity = this.form.querySelector('[name="quantity"]');
    this.variantControl = this.form.querySelector('[name="id"]');
    this.planControl = this.form.querySelector('[name="selling_plan"]');
    this.submit = this.form.querySelector('[type="submit"]');
    this.status = this.querySelector('[data-form-status]');
    this.quoteState = { status: 'loading' };
    this.quoteController = createQuoteController({ onState: state => { this.quoteState = state; this.renderQuote(); } });
    this.applyAddonSaving();
    this.form.addEventListener('submit', event => this.addToCart(event), { signal });
    this.form.addEventListener('change', event => {
      if (event.target.matches?.('[data-addon-toggle]')) {
        const box = event.target.closest('[data-addon]');
        box.classList.toggle('is-on', event.target.checked);
        const sizes = box.querySelector('[data-addon-sizes]'); if (sizes) sizes.hidden = !event.target.checked;
      }
      if (event.target === this.variantControl) this.changeVariant();
      this.updatePrice();
    }, { signal });
    this.quantity.addEventListener('input', () => this.updatePrice(), { signal });
    this.querySelectorAll('[data-quantity-step]').forEach(button => {
      button.addEventListener('click', () => {
        const step = Number(this.quantity.step) || 1;
        const min = Number(this.quantity.min) || 1;
        const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
        const current = Number(this.quantity.value) || min;
        this.quantity.value = Math.min(max, Math.max(min, current + Number(button.dataset.quantityStep) * step));
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal });
    });
    this.addEventListener('click', (event) => {
      const pick = event.target.closest('[data-nudge-pick]');
      if (!pick) return;
      event.preventDefault();
      this.querySelector(`[data-quantity-preset="${pick.dataset.nudgePick}"]`)?.click();
    }, { signal });
    this.querySelectorAll('[data-quantity-preset]').forEach(button => {
      button.addEventListener('click', () => {
        if (this.pending) return;
        const mixQty = this.mixItems().reduce((sum, entry) => sum + entry.qty, 0);
        this.quantity.value = Math.max(Number(this.quantity.min) || 1, Number(button.dataset.quantityPreset) - mixQty);
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal });
    });
    this.querySelector('[data-quote-retry]').addEventListener('click', () => {
      this.quoteController.refresh(this.quoteInput()).catch(() => {});
    }, { signal });
    document.addEventListener('cart:update', () => {
      if (!this.pending) { this.quoteKey = null; this.updatePrice(); }
    }, { signal });
    window.addEventListener('focus', () => {
      if (!this.pending) { this.quoteKey = null; this.updatePrice(); }
    }, { signal });
    this.mix = new Map();
    this.setupMix(signal);
    this.setupGallery(signal);
    this.setupSticky(signal);
    this.updatePrice();
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.quoteController?.destroy();
    this.stockRequest?.abort();
    this.galleryObserver?.disconnect();
    this.buyObserver?.disconnect();
    this.overlayObserver?.disconnect();
    this.controller = null;
  }

  get variant() { return this.product.variants.find(item => String(item.id) === this.variantControl.value); }
  get subscription() { return this.form.querySelector('[name="avl_purchase_mode"]:checked')?.value === 'subscription'; }
  get allocation() {
    if (!this.subscription) return null;
    return this.variant?.selling_plan_allocations?.find(item => String(item.selling_plan_id) === this.planControl?.value);
  }
  get plan() {
    return this.product.selling_plan_groups?.find(group => group.app_id === 'appstle' && group.name === 'My Subscription Plan')?.selling_plans.find(item => String(item.id) === this.planControl?.value);
  }
  format(cents) { return window.avlMoney(cents); }
  t(key, vars) { return window.avlT_ ? window.avlT_(`product.${key}`, vars) : key; }
  // The add-on offer price is a fixed DKK amount; recompute the saving in the active currency.
  applyAddonSaving() {
    this.querySelectorAll('[data-avl-addon-save]').forEach(element => {
      const save = Number(element.dataset.price) - window.avlConvert(Number(element.dataset.avlAddonSave));
      element.textContent = this.format(Math.max(0, save));
    });
  }
  write(selector, text) { this.querySelectorAll(selector).forEach(element => { element.textContent = text; }); }

  changeVariant() {
    const variant = this.variant;
    if (!variant) return;
    const rule = variant.quantity_rule || {};
    this.quantity.min = rule.min || 1;
    this.quantity.step = rule.increment || 1;
    if (rule.max != null) this.quantity.max = rule.max;
    else this.quantity.removeAttribute('max');
    this.quantity.value = rule.min || 1;
    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState(history.state, '', url);
    this.refreshStockState();
    if (variant.featured_media) {
      const media = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (media) this.scrollGallery([...this.gallery.children].indexOf(media));
    }
  }

  renderStock() {
    const state = stockForVariant(this.variant, this.stockMap);
    const node = this.querySelector('[data-stock-status]');
    if (node) { node.dataset.state = state.state; node.querySelector('[data-stock-text]').textContent = state.text; }
  }

  async refreshStockState() {
    this.stockRequest?.abort();
    const controller = new AbortController(); this.stockRequest = controller;
    const timer = setTimeout(() => controller.abort(), 12000);
    this.stockMap = {}; this.renderStock();
    try {
      const states = await refreshStock(this, controller.signal);
      if (this.stockRequest !== controller || !this.isConnected) return;
      this.stockMap = states;
      for (const variant of this.product.variants) {
        if (typeof states[String(variant.id)]?.available === 'boolean') variant.available = states[String(variant.id)].available;
      }
    } catch { /* Unknown physical inventory stays neutral; never keep a stale green claim. */ }
    finally { clearTimeout(timer); if(this.stockRequest === controller) { this.renderStock(); this.renderQuote(); } }
  }

  setupMix(signal) {
    this.querySelectorAll('[data-mix-variant]').forEach(link => {
      link.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        if (this.pending || link.hasAttribute('data-mix-soldout')) return;
        const id = Number(link.dataset.mixVariant);
        const entry = this.mix.get(id) || { id, name: link.dataset.mixName, price: Number(link.dataset.mixPrice), url: link.href, qty: 0 };
        entry.qty += 1;
        this.mix.set(id, entry);
        this.renderMix();
        this.updatePrice();
        this.announce(this.t('mix_added', { name: entry.name, n: entry.qty }));
      }, { signal });
    });
    this.querySelector('[data-mix-list]')?.addEventListener('click', event => {
      const button = event.target.closest('[data-mix-step]');
      if (!button || this.pending) return;
      const entry = this.mix.get(Number(button.dataset.mixId));
      if (!entry) return;
      entry.qty += Number(button.dataset.mixStep);
      if (entry.qty <= 0) this.mix.delete(entry.id);
      this.renderMix();
      this.updatePrice();
    }, { signal });
  }

  renderMix() {
    const box = this.querySelector('[data-mix]');
    if (!box) return;
    const list = box.querySelector('[data-mix-list]');
    list.replaceChildren();
    for (const entry of this.mix.values()) {
      const li = document.createElement('li');
      const name = document.createElement('a'); name.href = entry.url; name.textContent = entry.name; name.className = 'avlp-mix__name';
      const controls = document.createElement('div'); controls.className = 'avlp-mix__controls';
      const minus = document.createElement('button'); minus.type = 'button'; minus.dataset.mixStep = '-1'; minus.dataset.mixId = entry.id; minus.setAttribute('aria-label', this.t('mix_remove', { name: entry.name })); minus.textContent = '−';
      const out = document.createElement('output'); out.textContent = entry.qty;
      const plus = document.createElement('button'); plus.type = 'button'; plus.dataset.mixStep = '1'; plus.dataset.mixId = entry.id; plus.setAttribute('aria-label', this.t('mix_add', { name: entry.name })); plus.textContent = '+';
      controls.append(minus, out, plus);
      li.append(name, controls);
      list.append(li);
    }
    box.hidden = this.mix.size === 0;
    const hint = this.querySelector('[data-qty-hint]');
    if (hint) {
      const others = [...this.mix.values()].filter(e => e.qty > 0).map(e => `${e.qty} ${e.name.toLocaleLowerCase(window.avlLocale?.locale)}`);
      hint.hidden = !others.length;
      hint.textContent = others.length ? this.t('mix_hint', { list: others.join(', ') }) : '';
    }
    this.querySelectorAll('[data-mix-variant]').forEach(link => {
      const entry = this.mix.get(Number(link.dataset.mixVariant));
      const count = link.querySelector('[data-mix-count]');
      link.classList.toggle('is-mixed', Boolean(entry));
      if (count) { count.hidden = !entry; count.textContent = entry ? `${entry.qty}×` : ''; }
    });
  }

  mixItems() {
    return [...this.mix.values()].filter(entry => entry.qty > 0);
  }

  addonItems() {
    const box = this.querySelector('[data-addon]');
    if (!box || !box.querySelector('[data-addon-toggle]')?.checked) return [];
    const picked = box.querySelector('input[name="avl-addon-size"]:checked') || box.querySelector('input[type="hidden"][name="avl-addon-size"]');
    return picked ? [{ id: Number(picked.value), qty: 1 }] : [];
  }

  quoteInput() {
    const plan = this.subscription ? { sellingPlanId: this.planControl?.value } : {};
    return {
      items: [{ variantId: this.variant?.id, quantity: Number(this.quantity.value), ...plan },
        ...this.mixItems().map(entry => ({ variantId: entry.id, quantity: entry.qty, ...plan })),
        ...this.addonItems().map(entry => ({ variantId: entry.id, quantity: entry.qty }))],
      countryCode: this.dataset.country || 'DK', currency: this.dataset.currency || 'DKK',
      root: window.Shopify?.routes?.root || '/',
    };
  }

  updatePrice() {
    const variant = this.variant;
    if (!variant) return;
    const qty = Number(this.quantity.value) || 0;
    if (this.planControl) {
      this.querySelector('[data-plan-details]').hidden = !this.subscription;
      this.planControl.disabled = !this.subscription || this.pending;
      for (const option of this.planControl.options) {
        option.disabled = option.value === '692349469009' || !variant.selling_plan_allocations?.some(allocation => String(allocation.selling_plan_id) === option.value);
      }
      if (this.planControl.selectedOptions[0]?.disabled) {
        const enabled = [...this.planControl.options].find(option => !option.disabled);
        this.planControl.value = enabled?.value || '';
      }
    }
    const subscriptionAllocation = variant.selling_plan_allocations?.find(item => String(item.selling_plan_id) === this.planControl?.value);
    this.write('[data-once-price]', this.format(variant.price));
    this.write('[data-subscription-price]', subscriptionAllocation ? this.format(subscriptionAllocation.price) : '');
    const saving = this.querySelector('[data-subscription-saving]');
    if (saving) {
      const percentage = subscriptionAllocation && variant.price > 0
        ? Math.floor((variant.price - subscriptionAllocation.price) * 100 / variant.price) : 0;
      saving.hidden = percentage <= 0;
      saving.textContent = percentage > 0 ? this.t('sub_save', { pct: percentage }) : '';
    }
    // The headline is an undiscounted single-unit reference, clearly labeled.
    this.write('[data-unit-price]', this.format(variant.price));
    const compare = this.querySelector('[data-compare-price]');
    if (compare) {
      compare.hidden = !(variant.compare_at_price > variant.price);
      compare.textContent = this.format(variant.compare_at_price || variant.price);
    }
    const offer = this.querySelector('[data-quantity-offer]');
    if (offer) offer.hidden = this.subscription;
    const mixQty = this.mixItems ? this.mixItems().reduce((sum, entry) => sum + entry.qty, 0) : 0;
    const totalQty = qty + mixQty;
    const tiers = [...this.querySelectorAll('[data-quantity-preset]')].map(button => Number(button.dataset.quantityPreset));
    const activeTier = this.subscription ? 0 : Math.max(0, ...tiers.filter(count => totalQty >= count));
    const nextTier = tiers.filter(count => count > totalQty).sort((a, b) => a - b)[0];
    const offerNote = this.querySelector('[data-offer-progress]');
    if (offerNote) {
      offerNote.hidden = this.subscription || !totalQty;
      const pctOf = (count) => Number(this.querySelector(`[data-quantity-preset="${count}"]`)?.dataset.tierPct || 0);
      const unit = Number(this.variant?.price || 0);
      const saveNow = activeTier ? Math.floor(unit * totalQty * pctOf(activeTier) / 10000) : 0;
      const saveNext = nextTier ? Math.floor(unit * nextTier * pctOf(nextTier) / 10000) : 0;
      offerNote.textContent = [this.t(totalQty === 1 ? 'offer_one' : 'offer_other', { n: totalQty }),
        saveNow ? this.t('offer_save', { amount: this.format(saveNow * 100) }) : '',
        nextTier ? this.t('offer_next', { n: nextTier - totalQty, amount: this.format(saveNext * 100) }) : this.t('offer_max')].filter(Boolean).join(' · ');
    }
    const nudge = this.querySelector('[data-nudge]');
    if (nudge) {
      const pctOfN = (count) => Number(this.querySelector(`[data-quantity-preset="${count}"]`)?.dataset.tierPct || 0);
      const unitN = Number(this.variant?.price || 0);
      nudge.hidden = !!this.subscription || !nextTier;
      if (nextTier) {
        const pct = pctOfN(nextTier);
        const save = Math.floor(unitN * nextTier * pct / 10000);
        const left = nextTier - totalQty;
        const label = nudge.querySelector('[data-nudge-text]');
        const strong = document.createElement('strong'); strong.textContent = this.t('nudge_title', { n: nextTier, pct });
        const small = document.createElement('small'); small.textContent = totalQty > 1 ? this.t('nudge_left', { amount: this.format(save * 100), n: left }) : this.t('nudge_mix', { amount: this.format(save * 100) });
        label.replaceChildren(strong, small);
        const pick = nudge.querySelector('[data-nudge-pick]');
        pick.dataset.nudgePick = String(nextTier);
        pick.textContent = this.t('nudge_btn', { n: nextTier });
      }
    }
    this.querySelectorAll('[data-quantity-preset]').forEach(button => {
      const count = Number(button.dataset.quantityPreset);
      button.setAttribute('aria-pressed', String(count === activeTier));
      button.classList.toggle('is-reached', !this.subscription && totalQty >= count);
      button.disabled = this.pending || (this.quantity.max && count > Number(this.quantity.max)) || count < Number(this.quantity.min) || (count - Number(this.quantity.min)) % Number(this.quantity.step);
    });
    this.querySelector('[data-quantity-step="-1"]').disabled = this.pending || qty <= (Number(this.quantity.min) || 1);
    this.querySelector('[data-quantity-step="1"]').disabled = this.pending || Boolean(this.quantity.max && qty >= Number(this.quantity.max));
    const input = this.quoteInput();
    const key = JSON.stringify(input);
    if (key !== this.quoteKey) {
      this.quoteKey = key;
      this.quoteController.schedule(input);
    }
    this.renderQuote();
  }

  async loadCartTotal() {
    try {
      const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })).json();
      this.cartTotal = cart.total_price || 0;
    } catch { this.cartTotal = 0; }
    this.renderShip(this.lastSelectionTotal ?? null);
  }

  renderShip(selectionTotal) {
    const box = this.querySelector('[data-ship]');
    if (!box) return;
    if (this.cartTotal === undefined) { this.cartTotal = 0; this.loadCartTotal(); if (!this.shipBound) { this.shipBound = true; document.addEventListener('cart:update', () => this.loadCartTotal()); } }
    if (selectionTotal == null) return;
    this.lastSelectionTotal = selectionTotal;
    // data-ship-min is the free-shipping threshold in DKK (base) cents; cart totals are in the active currency.
    const min = window.avlConvert(Number(box.dataset.shipMin) || 0);
    if (!min) return;
    const total = (this.cartTotal || 0) + (this.subscription ? 0 : selectionTotal);
    const left = min - total;
    const text = box.querySelector('[data-ship-text]');
    text.replaceChildren();
    const strong = document.createElement('strong');
    // Locale strings carry a {{ amount }} slot; split around it so the amount stays in <strong>.
    const withStrong = (key, value) => {
      const [before, after = ''] = this.t(key, { amount: '\u0000' }).split('\u0000');
      strong.textContent = value;
      text.append(before, strong, after);
    };
    if (this.subscription) withStrong('ship_sub', this.t('ship_free'));
    else if (left > 0) withStrong('ship_left', this.format(left));
    else withStrong('ship_done', this.t('ship_free'));
    box.classList.toggle('is-done', this.subscription || left <= 0);
    box.querySelector('[data-ship-fill]').style.width = `${Math.max(4, Math.min(100, Math.round(total / min * 100)))}%`;
    box.hidden = false;
  }

  renderQuote() {
    if (!this.variant || !this.quoteState) return;
    this.renderStock();
    const { status, quote } = this.quoteState;
    const ready = status === 'ready' && quote && this.quantity.validity.valid;
    const available = this.variant.available && (!this.subscription || Boolean(this.allocation));
    this.submit.disabled = !available || !ready || this.pending || this.uncertain;
    this.submit.setAttribute('aria-busy', String(Boolean(this.pending || status === 'loading')));
    this.write('[data-total-price]', ready ? this.format(quote.total) : status === 'error' ? this.t('price_unavailable') : this.t('price_loading'));
    this.write('[data-submit-label]', this.pending ? this.t('adding') : !available ? this.t('sold_out') : ready ? this.t('add_total', { amount: this.format(quote.total) }) : status === 'error' ? this.t('confirm_price') : this.t('loading_yours'));
    this.write('[data-sticky-price]', ready ? this.t('sticky_total', { amount: this.format(quote.total) }) : this.t('sticky_choose'));
    this.querySelector('[data-quote-savings]').hidden = !ready || quote.savings <= 0;
    this.write('[data-savings-price]', ready ? this.format(quote.savings) : '');
    this.querySelector('[data-quote-retry]').hidden = status !== 'error';
    this.renderShip(ready ? quote.total : null);
    const note = status === 'error' ? this.quoteState.error?.message || this.t('quote_error') : ready
      ? [this.t(this.subscription ? 'note_sub' : 'note_once'), quote.includesExistingCart ? this.t('note_cart') : '', this.t('note_ship')].filter(Boolean).join(' ')
      : this.t('note_loading');
    this.write('[data-quote-note]', note);
    this.querySelector('[data-quote-note]').hidden = status !== 'error';
    if (this.subscription && this.allocation && this.plan) {
      const quantity = Number(this.quantity.value) || 0;
      const recurring = this.allocation.price_adjustments?.at(-1)?.price ?? this.allocation.per_delivery_price ?? this.allocation.price;
      this.write('[data-plan-terms]', [this.t('plan_qty', { n: quantity, plan: this.plan.name.toLocaleLowerCase(window.avlLocale?.locale) }),
        ready ? this.t('plan_first', { amount: this.format(quote.total) }) : '',
        this.t('plan_then', { amount: this.format(recurring * quantity) })].filter(Boolean).join(' '));
    }
  }

  setupGallery(signal) {
    this.gallery = this.querySelector('[data-gallery-track]');
    this.thumbnails = [...this.querySelectorAll('[data-gallery-index]')];
    this.activeMedia = 0;
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.scrollGallery(this.activeMedia - 1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.scrollGallery(this.activeMedia + 1), { signal });
    this.updateGalleryControls();
    this.thumbnails.forEach(button => button.addEventListener('click', () => this.scrollGallery(Number(button.dataset.galleryIndex)), { signal }));
    this.gallery.addEventListener('keydown', event => {
      if (event.target !== this.gallery || !['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
      event.preventDefault();
      this.scrollGallery(this.activeMedia + (event.key === 'ArrowRight' ? 1 : -1));
    }, { signal });
    this.galleryObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < .6) continue;
        this.activeMedia = [...this.gallery.children].indexOf(entry.target);
        this.updateGalleryControls();
        this.thumbnails.forEach((button, index) => {
          if (index === this.activeMedia) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        this.gallery.querySelectorAll('video').forEach(video => { if (!entry.target.contains(video)) video.pause(); });
      }
    }, { root: this.gallery, threshold: [.6] });
    [...this.gallery.children].forEach(media => this.galleryObserver.observe(media));
  }

  updateGalleryControls() {
    const previous = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (previous) previous.disabled = this.activeMedia === 0;
    if (next) next.disabled = this.activeMedia === this.gallery.children.length - 1;
    this.write('[data-gallery-count]', `${this.activeMedia + 1} / ${this.gallery.children.length}`);
  }

  scrollGallery(index) {
    const target = this.gallery.children[Math.max(0, Math.min(index, this.gallery.children.length - 1))];
    if (!target) return;
    this.gallery.scrollTo({ left: target.offsetLeft - this.gallery.children[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  setupSticky(signal) {
    const sticky = this.querySelector('[data-sticky-buy]');
    const controls = this.querySelector('[data-buy-controls]');
    this.controlsAbove = false;
    const update = () => {
      const blockingOverlay = [...document.querySelectorAll('dialog[open], [aria-modal="true"]:not([hidden]), #shopify-pc__banner:not([hidden])')].some(element => {
        const bounds = element.getBoundingClientRect();
        return element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden' && bounds.right > 0 && bounds.left < innerWidth && bounds.bottom > 0 && bounds.top < innerHeight;
      });
      const shouldHide = !this.controlsAbove || Boolean(blockingOverlay);
      if (sticky.hidden !== shouldHide) sticky.hidden = shouldHide;
    };
    this.buyObserver = new IntersectionObserver(entries => {
      this.controlsAbove = !entries[0].isIntersecting && entries[0].boundingClientRect.bottom < 0;
      update();
    }, { threshold: 0 });
    this.buyObserver.observe(controls);
    this.overlayObserver = new MutationObserver(update);
    this.overlayObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open', 'hidden', 'aria-modal'] });
    this.querySelector('[data-return-to-buy]').addEventListener('click', () => {
      this.form.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
      this.quantity.focus({ preventScroll: true });
    }, { signal });
  }

  announce(message, error = false) {
    this.status.textContent = message;
    this.status.dataset.error = String(error);
  }

  async readJson(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      const data = await response.json();
      if (!response.ok || data.status) {
        const error = new Error(typeof data.description === 'string' ? data.description : this.t('err_generic'));
        error.confirmedRejection = response.status >= 400 && response.status < 500;
        throw error;
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError') throw new Error(this.t('err_timeout'));
      if (error instanceof TypeError) throw new Error(this.t('err_network'));
      throw error;
    } finally { clearTimeout(timeout); }
  }

  async addToCart(event) {
    event.preventDefault();
    if (this.pending || this.uncertain || this.quoteState.status !== 'ready' || !this.form.reportValidity()) return;
    if (globalThis.__avlCartMutation) {
      this.announce(this.t('busy'), true);
      return;
    }
    const variantId = this.variant?.id;
    const quantity = Number(this.quantity.value);
    const planId = this.subscription ? Number(this.planControl.value) : null;
    if (!variantId || !Number.isSafeInteger(quantity) || quantity < 1 || (this.subscription && !this.allocation)) return;
    const oldPrice = this.allocation?.price ?? this.variant.price;
    const shownTotal = this.quoteState.quote.total;
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')].map(element => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
    let didPost = false;
    let rejection = false;
    let addResponse;
    globalThis.__avlCartMutation = true;
    this.pending = true;
    this.submit.setAttribute('aria-busy', 'true');
    this.announce(this.t('checking'));
    this.updatePrice();
    const locked = [...this.form.querySelectorAll('input:not([type=hidden]),select')].map(element => ({ element, disabled: element.disabled }));
    locked.forEach(({ element }) => { element.disabled = true; });
    try {
      // Shopify validates stock, price and quantity rules on cart/add itself, so we
      // add straight away instead of re-checking everything first (was ~2.5 s).
      const item = { id: variantId, quantity };
      if (planId) item.selling_plan = planId;
      const extras = [...this.mixItems().map(entry => ({ id: entry.id, quantity: entry.qty, ...(planId ? { selling_plan: planId } : {}) })),
        ...this.addonItems().map(entry => ({ id: entry.id, quantity: entry.qty }))];
      const totalQty = quantity + extras.reduce((sum, entry) => sum + entry.quantity, 0);
      this.announce(this.t('adding'));
      didPost = true;
      try {
        addResponse = await this.readJson(`${root}cart/add.js?appstle=true`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item, ...extras], sections: sectionIds, sections_url: location.pathname }),
        });
      } catch (error) { rejection = Boolean(error.confirmedRejection); throw error; }
      const added = (addResponse.items || []).some(line => line.variant_id === variantId);
      if (!added) throw new Error(this.t('err_confirm'));
      const cart = await this.readJson(`${root}cart.js`);
      this.dispatchEvent(new CartAddEvent(cart, String(variantId), {
        source: 'product-form-component', productId: String(this.product.id), variantId: String(variantId), itemCount: totalQty, sections: addResponse.sections,
      }));
      const drawer = document.getElementById('cart-drawer');
      if (typeof drawer?.open === 'function') drawer.open();
      else if (typeof document.querySelector('cart-drawer-component')?.open === 'function') document.querySelector('cart-drawer-component').open();
      else this.querySelector('[data-cart-fallback]').hidden = false;
      this.announce(this.t('added', { n: totalQty }));
      this.mix.clear();
      this.renderMix();
    } catch (error) {
      if (didPost) {
        // A lost response is not a safe reason to repeat a write.
        this.uncertain = !rejection;
        this.querySelector('[data-cart-fallback]').hidden = false;
        try {
          const cart = await this.readJson(`${root}cart.js`);
          this.dispatchEvent(new CartUpdateEvent(cart, this.id, { source: 'avl-product-recovery', itemCount: cart.item_count, didError: true }));
        } catch { /* Keep the standard cart link usable even while requests fail. */ }
      }
      this.refreshStockState();
      const message = this.uncertain ? this.t('err_uncertain') : error.message || this.t('err_add');
      this.announce(message, true);
      this.dispatchEvent(new CartErrorEvent(this.id, message, message, {}));
    } finally {
      locked.forEach(({ element, disabled }) => { element.disabled = disabled; });
      this.pending = false;
      globalThis.__avlCartMutation = false;
      this.submit.removeAttribute('aria-busy');
      if (didPost) this.quoteKey = null;
      this.updatePrice();
    }
  }
}

if (!customElements.get('avl-product')) customElements.define('avl-product', AvlProduct);

